import { useMemo, useState } from 'react'
import { ShoppingBag } from 'lucide-react'
import { formatarReais, validarGrupos } from '@du/shared'
import { Badge, Button, Escolha, GrupoEscolhas, Modal, QuantityStepper, Textarea } from '@du/ui'
import type { Produto } from '../lib/api.js'
import { useCarrinho } from '../lib/carrinho.js'

/**
 * Folha do produto: adicionais e observação.
 *
 * O total mostrado aqui é só uma PRÉVIA, somada no navegador a partir dos
 * preços que a API já mandou no catálogo. O valor que vale é o da cotação, no
 * carrinho — e os dois batem porque usam a mesma fonte. Mostrar prévia evita
 * uma ida ao servidor a cada clique num adicional, que no 4G seria lento.
 */
export function FolhaProduto({ produto, aoFechar }: { produto: Produto | null; aoFechar: () => void }) {
  const { adicionar } = useCarrinho()
  const [quantidade, setQuantidade] = useState(1)
  const [escolhidos, setEscolhidos] = useState<Record<string, string[]>>({})
  const [observacao, setObservacao] = useState('')

  // Reinicia ao trocar de produto: sem isto, os adicionais do lanche anterior
  // vazariam para o próximo.
  const chave = produto?.id ?? ''
  const [ultimo, setUltimo] = useState(chave)
  if (chave !== ultimo) {
    setUltimo(chave)
    setQuantidade(1)
    setEscolhidos({})
    setObservacao('')
  }

  const porAdicional = useMemo(() => {
    const m = new Map<string, { nome: string; precoCents: number; grupoId: string }>()
    for (const g of produto?.grupos ?? []) {
      for (const a of g.adicionais) m.set(a.id, { nome: a.nome, precoCents: a.precoCents, grupoId: g.id })
    }
    return m
  }, [produto])

  const selecionados = Object.values(escolhidos).flat()

  const veredito = useMemo(() => {
    if (!produto) return { ok: true as const }
    return validarGrupos(
      produto.grupos.map((g) => ({ id: g.id, nome: g.nome, min: g.min, max: g.max })),
      selecionados.map((id) => ({ grupoId: porAdicional.get(id)!.grupoId, quantidade: 1 })),
    )
  }, [produto, selecionados, porAdicional])

  const previaCents = produto
    ? (produto.precoCents + selecionados.reduce((s, id) => s + (porAdicional.get(id)?.precoCents ?? 0), 0)) *
      quantidade
    : 0

  const alternar = (grupo: Produto['grupos'][number], adicionalId: string) => {
    setEscolhidos((atuais) => {
      const doGrupo = atuais[grupo.id] ?? []
      const jaTem = doGrupo.includes(adicionalId)
      if (jaTem) return { ...atuais, [grupo.id]: doGrupo.filter((x) => x !== adicionalId) }
      // Grupo de escolha única troca em vez de acumular — é o caso do pão.
      if (grupo.max === 1) return { ...atuais, [grupo.id]: [adicionalId] }
      if (doGrupo.length >= grupo.max) return atuais
      return { ...atuais, [grupo.id]: [...doGrupo, adicionalId] }
    })
  }

  const confirmar = () => {
    if (!produto || !veredito.ok) return
    adicionar(produto, {
      quantidade,
      ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
      adicionais: selecionados.map((id) => ({
        adicionalId: id,
        nome: porAdicional.get(id)!.nome,
        quantidade: 1,
      })),
    })
    aoFechar()
  }

  return (
    <Modal
      aberto={produto !== null}
      aoFechar={aoFechar}
      titulo={produto?.nome ?? ''}
      folha
      tamanho="md"
      rodape={
        <div className="folha__rodape">
          <QuantityStepper valor={quantidade} onMudar={setQuantidade} />
          <Button
            icone={ShoppingBag}
            disabled={!veredito.ok || produto?.esgotado}
            onClick={confirmar}
            larguraTotal
          >
            Adicionar · {formatarReais(previaCents)}
          </Button>
        </div>
      }
    >
      {produto ? (
        <>
          {produto.fotoUrl ? (
            <img className="folha__foto" src={produto.fotoUrl} alt="" loading="lazy" />
          ) : null}
          {produto.ingredientes ? <p className="folha__ingredientes">{produto.ingredientes}</p> : null}
          {produto.combo.length > 0 ? (
            <p className="folha__combo">
              {produto.combo.map((c) => `${c.quantidade}× ${c.nome}`).join(' + ')}
            </p>
          ) : null}
          {produto.esgotado ? <Badge tom="erro">Esgotado hoje</Badge> : null}

          {produto.grupos.map((g) => (
            <GrupoEscolhas
              key={g.id}
              nome={g.nome}
              min={g.min}
              max={g.max}
              obrigatorio={g.obrigatorio}
              escolhidos={(escolhidos[g.id] ?? []).length}
            >
              {g.adicionais.map((a) => (
                <Escolha
                  key={a.id}
                  tipo={g.max === 1 ? 'radio' : 'checkbox'}
                  nome={g.id}
                  valor={a.id}
                  rotulo={a.nome}
                  {...(a.precoCents > 0 ? { precoExtra: `+ ${formatarReais(a.precoCents)}` } : {})}
                  esgotada={a.esgotado}
                  marcada={(escolhidos[g.id] ?? []).includes(a.id)}
                  onMudar={() => alternar(g, a.id)}
                />
              ))}
            </GrupoEscolhas>
          ))}

          <Textarea
            rotulo="Alguma observação?"
            placeholder="Sem cebola, ponto da carne, capricha na maionese…"
            maxLength={200}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
          />

          {!veredito.ok ? <p className="folha__erro">{veredito.mensagem}</p> : null}
        </>
      ) : null}
    </Modal>
  )
}
